import { CSSProperties, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import { etDateTime, linkTargetLabel } from './overviewModel';
import { useLifecycleToast } from './Toast';
import {
  Badge,
  C,
  FilledButton,
  FONT,
  Icon,
  OutlineButton,
  PATHS,
  text14,
  TextButton,
} from './ui';
import CopyNotices from '../CopyTab/CopyNotices';
import { LintIssue, lintVariant } from '../CopyTab/copyLint';
import { editableFieldsOf, variantNames } from '../CopyTab/copyModel';
import { summarizeProblems } from '../CopyTab/copyProblems';
import { Locked } from '../CopyTab/styles';
import useCopyMessage from '../CopyTab/useCopyMessage';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import styled from '@/styles';
import { CopyDraft } from '@/services/lifecycleAPI';
import { trackLifecycleFeatureUsed } from '@/util/vexo';

export type CopyTabId = 'sms' | 'push' | 'email';
type Message = ReturnType<typeof useCopyMessage>;

// Which modal tab edits which copy field.
export const TAB_FIELDS: Record<CopyTabId, string[]> = {
  sms: ['sms'],
  push: ['push_title', 'push_body'],
  email: ['subject_a', 'preview', 'body', 'button'],
};

// The words a toast uses for each tab (design LB, with SMS kept upper case).
const TAB_WORDS: Record<CopyTabId, string> = {
  sms: 'SMS',
  push: 'push notification',
  email: 'email',
};

const FIELD_LABELS: Record<string, string> = {
  sms: 'SMS message',
  push_title: 'Push notification title',
  push_body: 'Push notification body',
  // Only Subject A is edited here; B is in the full editor (Jira 10852 #66
  // Q3, Product's suggested answer). The full editor's own label.
  subject_a: 'Email subject A',
  preview: 'Preview text',
  body: 'Email body',
  button: 'Button name',
};

const fieldBase = {
  borderRadius: 12,
  border: 'none',
  boxShadow: `inset 0 0 0 1px ${C.fieldStroke}`,
  boxSizing: 'border-box',
  fontFamily: FONT,
  fontSize: 14,
  lineHeight: 1.4,
  color: C.black,
  outline: 'none',
  width: '100%',
  background: C.white,
  '&:focus': { boxShadow: `inset 0 0 0 1.5px ${C.green}` },
  '&[aria-invalid="true"]': { boxShadow: `inset 0 0 0 1.5px ${C.red}` },
  '&[readonly]': { background: C.scriptBg },
} as const;

const Area = styled('textarea', {
  ...fieldBase,
  height: 140,
  padding: 12,
  resize: 'none',
});

const Line = styled('input', { ...fieldBase, height: 40, padding: '0 12px' });

const labelStyle: CSSProperties = { ...text14, fontWeight: 600 };

// The email fields this window does not edit still belong to the email.
const EMAIL_ONLY_FIELDS = ['headline', 'subject_b'];

const fieldTab = (field: string): CopyTabId | null =>
  (Object.keys(TAB_FIELDS) as CopyTabId[]).find((tab) =>
    TAB_FIELDS[tab].includes(field),
  ) ?? (EMAIL_ONLY_FIELDS.includes(field) ? 'email' : null);

// graphy keeps one draft per message, for every channel and variant, and
// Publish or Discard takes all of it: the channels it changes.
export const draftChannels = (
  draft: CopyDraft | null,
  current: Record<string, Record<string, unknown>>,
): CopyTabId[] => {
  if (!draft) return [];
  const changed = new Set<CopyTabId>();
  Object.entries(draft.content ?? {}).forEach(([variant, content]) => {
    Object.entries(content ?? {}).forEach(([field, value]) => {
      const tab = fieldTab(field);
      if (
        tab &&
        String(value ?? '') !== String(current[variant]?.[field] ?? '')
      )
        changed.add(tab);
    });
  });
  return (Object.keys(TAB_FIELDS) as CopyTabId[]).filter((tab) =>
    changed.has(tab),
  );
};

const listWords = (tabs: CopyTabId[]): string => {
  const words = tabs.map((tab) => TAB_WORDS[tab]);
  return words.length > 1
    ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
    : words.join('');
};

export const tabsFor = (message: Message): CopyTabId[] => {
  const { detail } = message;
  if (!detail) return [];
  const fields = editableFieldsOf(detail, variantNames(detail)[0] ?? '');
  return (Object.keys(TAB_FIELDS) as CopyTabId[]).filter((tab) =>
    TAB_FIELDS[tab].some((field) => fields.includes(field)),
  );
};

type Props = {
  tab: CopyTabId;
  message: Message;
  journeyName: string;
  stepKey: string;
  canEdit: boolean;
  onWritten: () => void;
};

const CopyPanel = ({
  tab,
  message,
  journeyName,
  stepKey,
  canEdit,
  onWritten,
}: Props) => {
  const { toast } = useLifecycleToast();
  const { detail, text } = message;
  const navigate = useNavigate();
  const [guard, setGuard] = useState<'reset' | 'editor' | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // The saved draft is shared and not kept in History, so Discard always asks
  // once, as the full editor's does (CopyActions), and says when unsaved edits
  // go too. Without it, a click with no edits deleted another admin's draft.
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const names = useMemo(() => (detail ? variantNames(detail) : []), [detail]);
  const variant = names[0];

  const issues = useMemo(() => {
    const result: Record<string, LintIssue[]> = {};
    if (!detail) return result;
    names.forEach((name) => {
      result[name] = lintVariant({
        text: text[name] ?? {},
        allowed: detail.allowed_fields[name] ?? {},
        sampleValues: detail.sample_values,
        stopLine: detail.locked.sms_stop_line,
        locale: 'en',
      });
    });
    return result;
  }, [detail, names, text]);

  if (!detail || !variant) return null;

  const editable = editableFieldsOf(detail, variant);
  const fields = TAB_FIELDS[tab].filter((field) => editable.includes(field));
  const allIssues = names.flatMap((name) => issues[name] ?? []);
  const { problemTitle, listed } = summarizeProblems(
    detail,
    names,
    allIssues,
    message.saveError,
  );
  const draft = detail.draft;
  const live = (field: string) =>
    String(detail.current[variant]?.[field] ?? '');
  const shown = (field: string) =>
    String(text[variant]?.[field] ?? live(field));
  const savedDraft = (field: string) => {
    const saved = draft?.content?.[variant]?.[field];
    return saved !== undefined && String(saved) !== live(field);
  };
  const tabSavedDraft = fields.some(savedDraft);
  const isDraft =
    fields.some((field) => shown(field) !== live(field)) || tabSavedDraft;
  const latest = detail.history[0];
  const meta =
    tabSavedDraft && draft
      ? `Saved by ${draft.actor_name ?? 'a tenant admin'}, ${etDateTime(draft.updated_at)}`
      : isDraft
        ? 'Not saved yet'
        : latest
          ? `Published by ${latest.actor_name ?? 'a tenant admin'}, ${etDateTime(latest.published_at)}`
          : 'Default text from the code';

  const busy = message.saving;
  const otherVariantProblem = names
    .slice(1)
    .some(
      (name) =>
        (issues[name]?.length ?? 0) > 0 ||
        (draft?.lint_errors?.[name]?.length ?? 0) > 0,
    );
  const blocksSave = allIssues.some((i) => i.blocksSave);
  const resetOff = !canEdit || busy || detail.override === null;
  const discardOff = !canEdit || busy || !draft;
  const saveOff =
    !canEdit || busy || !(message.dirty || message.staleDraft) || blocksSave;
  const publishOff =
    !canEdit || busy || problemTitle !== null || (!message.dirty && !draft);
  const otherDraftTabs = draftChannels(draft, detail.current).filter(
    (other) => other !== tab,
  );
  const words = TAB_WORDS[tab];
  const allWords = otherDraftTabs.length
    ? `message (${listWords([tab, ...otherDraftTabs])})`
    : words;

  const openEditor = (e: { preventDefault: () => void }) => {
    trackLifecycleFeatureUsed({
      feature: 'full_editor_link',
      surface: 'step_modal',
      journey_key: detail.journey_key,
      step_key: detail.step_key,
      channel: tab,
    });
    if (!message.dirty) return;
    e.preventDefault();
    setGuard('editor');
  };
  const doDiscard = async () => {
    if (await message.discard()) {
      onWritten();
      toast('Draft discarded', `Showing the last published ${allWords}.`);
    }
  };
  const doReset = async () => {
    setConfirmReset(false);
    if (await message.reset()) {
      onWritten();
      toast(
        'Reset to default',
        `${journeyName} ${words} is back to the default text and live.`,
      );
    }
  };
  const doSave = async () => {
    if (await message.save()) {
      toast('Draft saved', `${journeyName} ${words} draft saved.`);
    }
  };
  const doPublish = async () => {
    if (await message.publish()) {
      onWritten();
      toast('Message published', `${journeyName} ${allWords} is now live.`);
    }
  };

  const fieldIssues = (field: string) =>
    (issues[variant] ?? [])
      .filter((i) => i.field === field)
      .map((i) => i.message);

  const renderField = (field: string) => {
    const value = text[variant]?.[field] ?? '';
    const problems = fieldIssues(field);
    const common = {
      'aria-invalid': problems.length > 0,
      'aria-label': FIELD_LABELS[field],
      'data-testid': `copyModalField-${field}`,
      readOnly: !canEdit,
      title: canEdit ? undefined : 'Tenant admin only',
      value,
      onChange: (e: { target: { value: string } }) =>
        message.edit(variant, field, e.target.value),
    };
    const multiline =
      field === 'sms' || field === 'push_body' || field === 'body';
    const first = field === fields[0];
    return (
      <div
        key={field}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginTop: !first && tab === 'push' ? 12 : 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={labelStyle}>{FIELD_LABELS[field]}</span>
          {first && <Badge draft={isDraft} />}
        </div>
        {multiline ? <Area {...common} /> : <Line {...common} />}
        {field === 'subject_a' && (
          <span style={{ ...text14, color: C.grey }}>
            Subject B (the A/B test):{' '}
            <Link
              data-testid="copyModalSubjectB"
              style={{
                color: C.green,
                fontWeight: 600,
                textDecoration: 'none',
              }}
              to={`?tab=copy&message=${stepKey}`}
              onClick={openEditor}
            >
              open the full editor
            </Link>
          </span>
        )}
        {field === 'sms' &&
          !String(detail.default[variant]?.sms ?? '').includes(
            detail.locked.sms_stop_line,
          ) && (
            <Locked>
              {`Added by the engine to a worker's first SMS: "${detail.locked.sms_stop_line}" Not editable.`}
            </Locked>
          )}
        {problems.map((problem) => (
          <span
            key={problem}
            role="alert"
            style={{ ...text14, color: C.red, fontWeight: 600 }}
          >
            {problem}
          </span>
        ))}
      </div>
    );
  };

  const emailBody = () => {
    const button = fields.includes('button');
    return (
      <>
        {fields.filter((f) => f !== 'button').map(renderField)}
        <div style={{ display: 'flex', gap: 20 }}>
          {button && (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <span style={labelStyle}>Button name</span>
              <Line
                aria-invalid={fieldIssues('button').length > 0}
                aria-label="Button name"
                data-testid="copyModalField-button"
                readOnly={!canEdit}
                title={canEdit ? undefined : 'Tenant admin only'}
                value={text[variant]?.button ?? ''}
                onChange={(e) =>
                  message.edit(variant, 'button', e.target.value)
                }
              />
              {/* The same problem line renderField gives every other field:
                  without it a cleared button blocked Save draft and Publish
                  with no reason shown (review D-06). */}
              {fieldIssues('button').map((problem) => (
                <span
                  key={problem}
                  role="alert"
                  style={{ ...text14, color: C.red, fontWeight: 600 }}
                >
                  {problem}
                </span>
              ))}
            </div>
          )}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <span style={labelStyle}>Links to</span>
            <div
              data-testid="copyModalLinksTo"
              style={{
                height: 40,
                borderRadius: 12,
                boxShadow: `inset 0 0 0 1px ${C.stroke}`,
                padding: '0 12px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                ...text14,
                color: C.light,
              }}
            >
              {linkTargetLabel(stepKey)}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tab === 'email' ? 20 : 12,
        }}
      >
        {tab === 'email' ? emailBody() : fields.map(renderField)}
        <div
          data-testid="copyModalMeta"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            ...text14,
            color: C.grey,
            marginTop: tab === 'email' ? 12 : 0,
          }}
        >
          <span>{meta}</span>
          <span title="Everyone with the tenant admin role edits the same draft.">
            <Icon d={PATHS.info} size={18} />
          </span>
          <Link
            data-testid="copyModalFullEditor"
            style={{
              marginLeft: 'auto',
              color: C.green,
              fontWeight: 600,
              textDecoration: 'none',
            }}
            to={`?tab=copy&message=${stepKey}`}
            onClick={openEditor}
          >
            Open full editor
          </Link>
        </div>
        <CopyNotices
          showError={false}
          conflict={message.conflict}
          draftConflict={message.draftConflict}
          error={null}
          notice={null}
          onReload={message.retry}
        />
        {/* Publish re-saves a draft from before a revert or reset on the live
            version (BH9-01), so say so here as the full editor does, or the
            revert is undone without a warning (review D-01). */}
        {draft && message.staleDraft && (
          <div
            data-testid="copyModalStaleDraft"
            role="status"
            style={{ ...text14, color: C.red }}
          >
            {`This draft was saved on version ${draft.base_version}. Check it, then publish.`}
          </div>
        )}
        {(problemTitle || listed.length > 0) && (
          <div
            data-testid="copyModalProblems"
            role="alert"
            style={{
              ...text14,
              color: C.red,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {problemTitle && <strong>{problemTitle}</strong>}
            {listed.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        )}
        {canEdit && otherDraftTabs.length > 0 && (
          <div
            data-testid="copyModalDraftOtherChannels"
            style={{ ...text14, color: C.black }}
          >
            The saved draft also changes the {listWords(otherDraftTabs)}.
            Publish and Discard apply to all of it.
          </div>
        )}
        {otherVariantProblem && (
          <div
            data-testid="copyModalOtherVariants"
            style={{ ...text14, color: C.black }}
          >
            Another variant has a problem.{' '}
            <Link
              style={{ color: C.green, fontWeight: 600 }}
              to={`?tab=copy&message=${stepKey}`}
              onClick={openEditor}
            >
              Fix it in the full editor
            </Link>
            .
          </div>
        )}
      </div>
      <div
        data-testid="copyModalFooter"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 16,
          margin: '60px -24px 0',
        }}
      >
        <TextButton
          data-testid="copyModalReset"
          disabled={resetOff}
          type="button"
          onClick={() =>
            message.dirty ? setGuard('reset') : setConfirmReset(true)
          }
        >
          Reset to default
        </TextButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <TextButton
            data-testid="copyModalDiscard"
            disabled={discardOff}
            type="button"
            onClick={() => setConfirmDiscard(true)}
          >
            Discard draft
          </TextButton>
          <OutlineButton
            data-testid="copyModalSave"
            disabled={saveOff}
            type="button"
            onClick={doSave}
          >
            Save draft
          </OutlineButton>
          <FilledButton
            data-testid="copyModalPublish"
            disabled={publishOff}
            type="button"
            onClick={doPublish}
          >
            Publish
          </FilledButton>
        </div>
      </div>
      {guard &&
        createPortal(
          <ConfirmationModal
            disableClickout
            cancelLabel="Keep editing"
            confirmLabel="Discard"
            message="Your edits to this message are not saved."
            subMessage={null}
            title="Discard unsaved changes?"
            onCancel={() => setGuard(null)}
            onConfirm={() => {
              const next = guard;
              setGuard(null);
              if (next === 'reset') setConfirmReset(true);
              else navigate(`?tab=copy&message=${stepKey}`);
            }}
          />,
          document.body,
        )}
      {confirmDiscard &&
        createPortal(
          <ConfirmationModal
            disableClickout
            cancelLabel="Keep the draft"
            confirmLabel="Discard"
            message={`The draft of ${detail.label} is deleted for everyone. Drafts are not kept in History.${message.dirty ? ' Your unsaved edits are lost too.' : ''}`}
            subMessage={null}
            title="Discard the saved draft?"
            onCancel={() => setConfirmDiscard(false)}
            onConfirm={() => {
              setConfirmDiscard(false);
              doDiscard();
            }}
          />,
          document.body,
        )}
      {confirmReset &&
        createPortal(
          <ConfirmationModal
            disableClickout
            cancelLabel="Cancel"
            confirmLabel="Reset"
            message={`${detail.label} goes back to the text in the code and goes live now. The current text stays in History, so you can revert.${draft ? ' The saved draft is discarded too.' : ''}`}
            subMessage={null}
            title="Reset to the default text?"
            onCancel={() => setConfirmReset(false)}
            onConfirm={doReset}
          />,
          document.body,
        )}
    </>
  );
};

export default CopyPanel;
